/**
 * P1-2 微信聊天 AI 解析弹框
 *
 * 左侧：粘贴聊天记录
 * 右侧：AI 实时分析结果（意图/关键信息/推荐话术）
 * 底部：一键写入跟进记录（POST /follow-ups）
 *
 * 后端复用 /follow-ups/analyze（feature_key=follow_up_analysis）+ /follow-ups
 * 区别于 /follow-ups/new 页面：弹框形式，不离开客户详情页
 */
import { useState } from 'react';
import { Modal, Input, message, Spin } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { Btn, Pill } from './ui';
import { fmtMonthDayTime } from '@/lib/format';

interface Props {
  open: boolean;
  customerId: number;
  customerName: string;
  currentStage: string;
  onClose: () => void;
  onSaved?: () => void;
}

interface Analysis {
  summary: string;
  extractedInfo?: {
    mentionedPrice?: string | null;
    mentionedTime?: string | null;
    painPoints?: string[];
    objections?: string[];
    competitorsMentioned?: string[];
    decisionSignals?: string[];
  };
  currentStageSuggestion?: string;
  stageChangeReason?: string;
  customerMood?: 'positive' | 'neutral' | 'hesitant' | 'resistant';
  moodReason?: string;
  nextFollowupAt?: string;
  nextFollowupReason?: string;
  recommendedScripts?: { title: string; content: string; reason: string }[];
  healthScoreDelta?: number;
  qualityScore?: number;
  warningFlags?: string[];
}

const MOOD_LABEL: Record<string, string> = {
  positive: '积极', neutral: '中性', hesitant: '犹豫', resistant: '抵触',
};
const MOOD_TONE: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
  positive: 'success', neutral: 'default', hesitant: 'warning', resistant: 'danger',
};

export function ChatAnalysisModal({ open, customerId, customerName, currentStage, onClose, onSaved }: Props) {
  const [chatLog, setChatLog] = useState('');
  const [analysis, setAnalysis] = useState<Analysis | null>(null);

  const analyze = useMutation({
    mutationFn: () => $.post<Analysis>('/follow-ups/analyze', { customerId, chatLog }),
    onSuccess: (r) => {
      setAnalysis(r);
      message.success('AI 分析完成');
    },
    onError: () => {/* api.ts 已 toast */},
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!analysis) throw new Error('请先解析');
      // 把 AI 分析的 summary + 推荐话术拼成跟进正文
      const scripts = (analysis.recommendedScripts ?? []).map((s) => `· ${s.title}：${s.content}`).join('\n');
      const content = `${analysis.summary}\n\n${scripts}`.trim();
      return $.post('/follow-ups', {
        customerId,
        content,
        chatLogRaw: chatLog,
        stage: analysis.currentStageSuggestion,
        nextFollowupAt: analysis.nextFollowupAt,
        aiAnalysisJson: analysis,
        aiSummary: analysis.summary,
        aiMood: analysis.customerMood,
        applyAiSuggestion: true,
      });
    },
    onSuccess: () => {
      message.success('已写入跟进记录');
      setChatLog('');
      setAnalysis(null);
      onSaved?.();
      onClose();
    },
  });

  const reset = () => { setChatLog(''); setAnalysis(null); };

  return (
    <Modal
      title={<>AI 解析聊天记录 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 8 }}>{customerName} · 当前 {currentStage}</span></>}
      open={open}
      width={1100}
      onCancel={() => { reset(); onClose(); }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {analysis ? `质量分 ${analysis.qualityScore ?? '—'} / 健康度 Δ ${analysis.healthScoreDelta ?? 0}` : '粘贴聊天记录后点"AI 解析"'}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn kind="default" onClick={() => { reset(); onClose(); }}>取消</Btn>
            <Btn kind="default" onClick={() => analyze.mutate()}
                 disabled={!chatLog || chatLog.length < 20 || analyze.isPending}>
              {analyze.isPending ? '解析中…' : 'AI 解析'}
            </Btn>
            <Btn kind="primary" onClick={() => save.mutate()}
                 disabled={!analysis || save.isPending}>
              {save.isPending ? '保存中…' : '一键写入跟进'}
            </Btn>
          </div>
        </div>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, minHeight: 460 }}>
        {/* 左侧：粘贴聊天记录 */}
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>
            粘贴微信聊天记录（建议 200 字以上）
          </div>
          <Input.TextArea
            autoFocus
            value={chatLog}
            onChange={(e) => setChatLog(e.target.value)}
            placeholder={`例如：\n客户：老师好，我想了解一下你们的课程\n销售：好的～方便先聊聊您的目标吗？\n客户：我想考职业资格证，预算大概 2 万...`}
            rows={20}
            style={{ resize: 'none', fontFamily: 'inherit' }}
          />
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-3)' }}>
            {chatLog.length} 字符
          </div>
        </div>

        {/* 右侧：AI 分析结果 */}
        <div style={{
          background: 'var(--bg-2)', borderRadius: 6, padding: 14,
          overflowY: 'auto', maxHeight: 500,
        }}>
          {analyze.isPending && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
              <Spin tip="AI 分析中…" />
            </div>
          )}
          {!analyze.isPending && !analysis && (
            <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: '60px 16px', fontSize: 13 }}>
              ← 左侧粘贴聊天记录后点"AI 解析"<br />
              AI 将自动提取意图 / 关键信息 / 推荐话术
            </div>
          )}
          {analysis && (
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              {/* 摘要 */}
              <Section title="客户意图摘要">
                <div>{analysis.summary}</div>
              </Section>

              {/* 阶段建议 + 情绪 */}
              <Section title="状态判断">
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                  {analysis.currentStageSuggestion && (
                    <Pill tone="primary">建议阶段 {analysis.currentStageSuggestion}</Pill>
                  )}
                  {analysis.customerMood && (
                    <Pill tone={MOOD_TONE[analysis.customerMood]}>
                      情绪 {MOOD_LABEL[analysis.customerMood]}
                    </Pill>
                  )}
                </div>
                {analysis.stageChangeReason && (
                  <div style={{ color: 'var(--text-3)', fontSize: 12 }}>
                    阶段理由：{analysis.stageChangeReason}
                  </div>
                )}
                {analysis.moodReason && (
                  <div style={{ color: 'var(--text-3)', fontSize: 12 }}>
                    情绪理由：{analysis.moodReason}
                  </div>
                )}
              </Section>

              {/* 关键信息 */}
              {analysis.extractedInfo && (
                <Section title="关键信息">
                  <Kv label="预算" value={analysis.extractedInfo.mentionedPrice} />
                  <Kv label="时间" value={analysis.extractedInfo.mentionedTime} />
                  <ListLine label="痛点" arr={analysis.extractedInfo.painPoints} />
                  <ListLine label="顾虑" arr={analysis.extractedInfo.objections} />
                  <ListLine label="提到竞品" arr={analysis.extractedInfo.competitorsMentioned} />
                  <ListLine label="决策信号" arr={analysis.extractedInfo.decisionSignals} highlight />
                </Section>
              )}

              {/* 推荐话术 */}
              {analysis.recommendedScripts && analysis.recommendedScripts.length > 0 && (
                <Section title={`推荐话术 (${analysis.recommendedScripts.length})`}>
                  {analysis.recommendedScripts.map((s, i) => (
                    <div key={i} style={{
                      background: 'var(--bg-1)', padding: 10, borderRadius: 4,
                      marginBottom: 6, borderLeft: '3px solid var(--purple)',
                    }}>
                      <div style={{ fontWeight: 500, fontSize: 12, marginBottom: 4 }}>{s.title}</div>
                      <div style={{ fontSize: 12 }}>{s.content}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{s.reason}</div>
                    </div>
                  ))}
                </Section>
              )}

              {/* 下次跟进建议 */}
              {analysis.nextFollowupAt && (
                <Section title="下次跟进">
                  <div>{fmtMonthDayTime(analysis.nextFollowupAt)}</div>
                  {analysis.nextFollowupReason && (
                    <div style={{ color: 'var(--text-3)', fontSize: 12 }}>{analysis.nextFollowupReason}</div>
                  )}
                </Section>
              )}

              {/* 警示 */}
              {analysis.warningFlags && analysis.warningFlags.length > 0 && (
                <Section title="⚠️ 警示">
                  {analysis.warningFlags.map((w, i) => (
                    <div key={i} style={{ color: 'var(--danger)', fontSize: 12 }}>· {w}</div>
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 11, color: 'var(--text-3)', fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4,
      }}>{title}</div>
      {children}
    </div>
  );
}

function Kv({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 6, fontSize: 12, marginBottom: 2 }}>
      <span style={{ color: 'var(--text-3)', minWidth: 50 }}>{label}：</span>
      <span>{value}</span>
    </div>
  );
}

function ListLine({ label, arr, highlight }: { label: string; arr?: string[]; highlight?: boolean }) {
  if (!arr || arr.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 6, fontSize: 12, marginBottom: 2 }}>
      <span style={{ color: 'var(--text-3)', minWidth: 50 }}>{label}：</span>
      <span style={{ color: highlight ? 'var(--success)' : 'inherit' }}>
        {arr.join(' · ')}
      </span>
    </div>
  );
}
